import { create } from 'zustand';

export type QRType = 
  | 'url' 
  | 'vcard' 
  | 'wifi' 
  | 'email' 
  | 'sms' 
  | 'whatsapp' 
  | 'phone' 
  | 'location' 
  | 'text' 
  | 'calendar' 
  | 'paypal' 
  | 'bitcoin' 
  | 'appstore' 
  | 'pdf' 
  | 'instagram' 
  | 'twitter' 
  | 'linkedin'
  | 'ar'
  | 'bio_page'
  | 'ai_art'
  | 'gamification'
  | 'blockchain';

export type DotsType = 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'square' | 'extra-rounded';
export type CornersSquareType = 'dot' | 'square' | 'extra-rounded' | 'rounded' | 'dots' | 'classy' | 'classy-rounded';
export type CornersDotType = 'dot' | 'square' | 'rounded' | 'dots' | 'extra-rounded' | 'classy' | 'classy-rounded';
export type GradientType = 'linear' | 'radial';
export type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H';
export type DownloadFormat = 'png' | 'svg';
export type FrameStyle = 'none' | 'simple' | 'rounded' | 'circle' | 'scan-me-top' | 'scan-me-bottom' | 'phone' | 'instagram' | 'tag' | 'double' | 'decorative' | 'badge' | 'ticket' | 'gradient' | 'neon' | 'clipboard' | 'laptop' | 'watch' | 'camera' | 'bag' | 'speech-bubble' | 'banner';

export interface GradientSettings {
  enabled: boolean;
  type: GradientType;
  color1: string;
  color2: string;
  rotation: number;
}

export interface LogoSettings {
  enabled: boolean;
  url: string;
  size: number;
  transparentBackground: boolean;
}

export interface FrameSettings {
  style: FrameStyle;
  text: string;
  color: string;
  thickness: number;
  textPosition: 'top' | 'bottom' | 'inside';
}

export interface QRDesignSettings {
  // Colors
  dotsColor: string;
  backgroundColor: string;
  backgroundTransparent: boolean;
  gradient: GradientSettings;
  cornersSquareColor: string;
  cornersDotColor: string;
  
  // Shape
  dotsType: DotsType;
  cornersSquareType: CornersSquareType;
  cornersDotType: CornersDotType;
  
  // Logo
  logo: LogoSettings;
  
  // Frame
  frame: FrameSettings;
  
  // Advanced
  errorCorrectionLevel: ErrorCorrectionLevel;
  width: number;
  height: number;
  margin: number;
  downloadFormat: DownloadFormat;
  resolution: number;
}

export interface QRCodeData {
  id?: string;
  name: string;
  type: QRType;
  contentData: Record<string, string>;
  designSettings: QRDesignSettings;
  isDynamic: boolean;
  routingRules?: Array<{
    id: string;
    type: 'time' | 'os' | 'device' | 'country' | 'language';
    condition: Record<string, unknown>;
    destination: string;
  }>;
  analyticsConfig?: {
    ga4_id?: string;
    fb_pixel_id?: string;
    google_ads_id?: string;
    custom_script?: string;
  };
  blockchainConfig?: {
    enabled: boolean;
    network: string;
    collection_name?: string;
    mint_on_scan: boolean;
    nft_gating?: {
      enabled: boolean;
      contract_address: string;
      min_balance: number;
    };
    nft_metadata?: {
      name: string;
      description: string;
      image: string;
    };
  };
}

interface QRStore {
  currentQR: QRCodeData;
  savedQRs: QRCodeData[];
  viewMode: 'grid' | 'list';
  activeDesignTab: string;
  setCurrentQR: (qr: Partial<QRCodeData>) => void;
  setType: (type: QRType) => void;
  setContentData: (data: Record<string, string>) => void;
  setDesignSettings: (settings: Partial<QRDesignSettings>) => void;
  setGradientSettings: (settings: Partial<GradientSettings>) => void;
  setLogoSettings: (settings: Partial<LogoSettings>) => void;
  setFrameSettings: (settings: Partial<FrameSettings>) => void;
  setViewMode: (mode: 'grid' | 'list') => void;
  setActiveDesignTab: (tab: string) => void;
  applyColorPreset: (preset: ColorPreset) => void;
  resetQR: () => void;
}

export interface ColorPreset {
  id: string;
  name: string;
  dotsColor: string;
  backgroundColor: string;
  gradient?: GradientSettings;
}

export const colorPresets: ColorPreset[] = [
  {
    id: 'tech-futuristic',
    name: 'Tech Futuristic',
    dotsColor: '#00BFFF',
    backgroundColor: '#001F3F',
    gradient: { enabled: true, type: 'linear', color1: '#001F3F', color2: '#007BFF', rotation: 135 }
  },
  {
    id: 'eco-friendly',
    name: 'Eco-Friendly',
    dotsColor: '#228B22',
    backgroundColor: '#90EE90',
    gradient: { enabled: true, type: 'radial', color1: '#90EE90', color2: '#F0FFF0', rotation: 0 }
  },
  {
    id: 'vibrant-party',
    name: 'Vibrant Party',
    dotsColor: '#FF4500',
    backgroundColor: '#FF69B4',
    gradient: { enabled: true, type: 'linear', color1: '#FF69B4', color2: '#FFD700', rotation: 45 }
  },
  {
    id: 'minimalist-pro',
    name: 'Minimalist Pro',
    dotsColor: '#000000',
    backgroundColor: '#FFFFFF',
    gradient: { enabled: false, type: 'linear', color1: '#FFFFFF', color2: '#FFFFFF', rotation: 0 }
  },
  {
    id: 'luxury-elegant',
    name: 'Luxury Elegant',
    dotsColor: '#FFD700',
    backgroundColor: '#4B0082',
    gradient: { enabled: true, type: 'linear', color1: '#4B0082', color2: '#6B238E', rotation: 135 }
  },
  {
    id: 'ocean-calm',
    name: 'Ocean Calm',
    dotsColor: '#20B2AA',
    backgroundColor: '#ADD8E6',
    gradient: { enabled: true, type: 'linear', color1: '#ADD8E6', color2: '#FFFFFF', rotation: 180 }
  },
  {
    id: 'sunset-warm',
    name: 'Sunset Warm',
    dotsColor: '#FF6B35',
    backgroundColor: '#FFF0E5',
    gradient: { enabled: true, type: 'linear', color1: '#FFD93D', color2: '#FF6B6B', rotation: 45 }
  },
  {
    id: 'neon-glow',
    name: 'Neon Glow',
    dotsColor: '#39FF14',
    backgroundColor: '#0D0D0D',
    gradient: { enabled: false, type: 'linear', color1: '#0D0D0D', color2: '#0D0D0D', rotation: 0 }
  },
  {
    id: 'corporate-blue',
    name: 'Corporate Blue',
    dotsColor: '#0066CC',
    backgroundColor: '#F5F8FC',
    gradient: { enabled: false, type: 'linear', color1: '#F5F8FC', color2: '#F5F8FC', rotation: 0 }
  },
  {
    id: 'vintage-sepia',
    name: 'Vintage Sepia',
    dotsColor: '#704214',
    backgroundColor: '#F5E6D3',
    gradient: { enabled: true, type: 'radial', color1: '#F5E6D3', color2: '#E8D5C4', rotation: 0 }
  },
  {
    id: 'pastel-soft',
    name: 'Pastel Soft',
    dotsColor: '#B19CD9',
    backgroundColor: '#FFF0F5',
    gradient: { enabled: true, type: 'linear', color1: '#E0BBE4', color2: '#FEC8D8', rotation: 90 }
  },
  {
    id: 'monochrome',
    name: 'Monochrome',
    dotsColor: '#333333',
    backgroundColor: '#E5E5E5',
    gradient: { enabled: false, type: 'linear', color1: '#E5E5E5', color2: '#E5E5E5', rotation: 0 }
  }
];

const defaultDesignSettings: QRDesignSettings = {
  dotsColor: '#1a1a2e',
  backgroundColor: '#ffffff',
  backgroundTransparent: false,
  gradient: {
    enabled: false,
    type: 'linear',
    color1: '#ffffff',
    color2: '#f0f0f0',
    rotation: 0
  },
  cornersSquareColor: '#1a1a2e',
  cornersDotColor: '#1a1a2e',
  dotsType: 'rounded',
  cornersSquareType: 'extra-rounded',
  cornersDotType: 'dot',
  logo: {
    enabled: false,
    url: '',
    size: 25,
    transparentBackground: true
  },
  frame: {
    style: 'none',
    text: 'SCAN ME',
    color: '#1a1a2e',
    thickness: 20,
    textPosition: 'bottom'
  },
  errorCorrectionLevel: 'M',
  width: 300,
  height: 300,
  margin: 10,
  downloadFormat: 'png',
  resolution: 1000
};

const defaultQR: QRCodeData = {
  name: '',
  type: 'url',
  contentData: {},
  designSettings: defaultDesignSettings,
  isDynamic: false,
};

export const useQRStore = create<QRStore>((set) => ({
  currentQR: defaultQR,
  savedQRs: [],
  viewMode: 'grid',
  activeDesignTab: 'colors',
  
  setCurrentQR: (qr) =>
    set((state) => ({
      currentQR: { ...state.currentQR, ...qr },
    })),
    
  setType: (type) =>
    set((state) => ({
      currentQR: { ...state.currentQR, type, contentData: {} },
    })),
    
  setContentData: (data) =>
    set((state) => ({
      currentQR: { ...state.currentQR, contentData: { ...state.currentQR.contentData, ...data } },
    })),
    
  setDesignSettings: (settings) =>
    set((state) => ({
      currentQR: {
        ...state.currentQR,
        designSettings: { ...state.currentQR.designSettings, ...settings },
      },
    })),

  setGradientSettings: (settings) =>
    set((state) => ({
      currentQR: {
        ...state.currentQR,
        designSettings: {
          ...state.currentQR.designSettings,
          gradient: { ...state.currentQR.designSettings.gradient, ...settings },
        },
      },
    })),

  setLogoSettings: (settings) =>
    set((state) => ({
      currentQR: {
        ...state.currentQR,
        designSettings: {
          ...state.currentQR.designSettings,
          logo: { ...state.currentQR.designSettings.logo, ...settings },
        },
      },
    })),

  setFrameSettings: (settings) =>
    set((state) => ({
      currentQR: {
        ...state.currentQR,
        designSettings: {
          ...state.currentQR.designSettings,
          frame: { ...state.currentQR.designSettings.frame, ...settings },
        },
      },
    })),
    
  setViewMode: (mode) => set({ viewMode: mode }),

  setActiveDesignTab: (tab) => set({ activeDesignTab: tab }),

  applyColorPreset: (preset) =>
    set((state) => ({
      currentQR: {
        ...state.currentQR,
        designSettings: {
          ...state.currentQR.designSettings,
          dotsColor: preset.dotsColor,
          backgroundColor: preset.backgroundColor,
          cornersSquareColor: preset.dotsColor,
          cornersDotColor: preset.dotsColor,
          gradient: preset.gradient || state.currentQR.designSettings.gradient,
        },
      },
    })),
  
  resetQR: () => set({ currentQR: defaultQR }),
}));
